// Cliente HTTP de la bandeja (PP-34), mismo patron que sites/http.ts y
// fleet/http.ts: token en el header, sin cookies ni redirecciones, y toda
// respuesta por el parser de @ucorp/contracts antes de tocar estado. Los
// cuerpos que salen tambien pasan por el parser: lo que no cumple el contrato
// no viaja.
//
// Aislamiento entre proveedores: la garantia es de la API (proyecta solo los
// viajes del proveedor de la habilitacion). Aca se repite como defensa: un
// viaje de otro proveedor nunca llega a la pantalla, aunque la respuesta lo
// traiga. Si un viaje paso a otro proveedor, simplemente deja de estar.
import {
  AcceptTripOfferRequest,
  AssignTripRequest,
  AssignTripResponse,
  ErrorResponse,
  ProviderOffersResponse,
  ProviderTripDetailResponse,
  ProviderTripOfferResponse,
  ProviderTripsResponse,
  RejectTripOfferRequest,
  RejectTripOfferResponse,
  type ProviderTrip,
} from "@ucorp/contracts";
import type { PortalSession } from "../auth/session.js";
import { RequestsError, type RequestsErrorKind, type RequestsPort, type RequestsSite } from "./port.js";

type Method = "GET" | "POST" | "PUT";

/** Tope de paginas por consulta: 4 x 200 viajes de un proveedor en un dia es de sobra. */
const PAGE_SIZE = 200;
const MAX_PAGES = 4;

const TYPED_CONFLICTS: ReadonlySet<RequestsErrorKind> = new Set([
  "offer_not_open",
  "offer_not_yours",
  "trip_not_assignable",
  "roster_entry_unknown",
]);

function failure(status: number, body: unknown): RequestsError {
  if (status === 401 || status === 403) return new RequestsError("access");
  if (status === 404) return new RequestsError("not_found");
  if (status === 400) return new RequestsError("validation");
  if (status === 409) {
    // Solo el `type` del sobre, nunca el `detail`.
    const envelope = ErrorResponse.safeParse(body);
    const type = envelope.success ? (envelope.data.error.type as RequestsErrorKind) : undefined;
    return new RequestsError(type !== undefined && TYPED_CONFLICTS.has(type) ? type : "conflict");
  }
  return new RequestsError("unavailable");
}

/** Lo minimo de un schema de contracts, sin depender de zod desde el portal. */
interface Schema<T> {
  safeParse(value: unknown): { success: true; data: T } | { success: false };
}

function parseOrFail<T>(schema: Schema<T>, value: unknown, kind: "validation" | "unavailable"): T {
  const result = schema.safeParse(value);
  if (!result.success) throw new RequestsError(kind);
  return result.data;
}

export function createHttpRequestsPort(apiUrl: string, request: typeof fetch = fetch): RequestsPort {
  const base = apiUrl.replace(/\/+$/, "");

  async function call(session: PortalSession, method: Method, path: string, body?: unknown): Promise<unknown> {
    let response: Response;
    try {
      response = await request(`${base}${path}`, {
        method,
        headers: {
          Authorization: `Bearer ${session.accessToken}`,
          ...(body === undefined ? {} : { "Content-Type": "application/json" }),
        },
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
        cache: "no-store",
        credentials: "omit",
        redirect: "error",
      });
    } catch {
      throw new RequestsError("unavailable");
    }
    const payload: unknown = await response.json().catch(() => undefined);
    if (!response.ok) throw failure(response.status, payload);
    return payload;
  }

  const trips = (s: RequestsSite) => `/sites/${encodeURIComponent(s.siteId)}/provider/trips`;
  const offers = (s: RequestsSite) => `/sites/${encodeURIComponent(s.siteId)}/provider/offers`;
  const trip = (s: RequestsSite, tripId: string) => `${trips(s)}/${encodeURIComponent(tripId)}`;

  /** Un viaje de otro proveedor en una respuesta es una respuesta rota. */
  function own(s: RequestsSite, row: ProviderTrip): ProviderTrip {
    if (row.providerId !== s.providerId || row.siteId !== s.siteId) throw new RequestsError("unavailable");
    return row;
  }

  return {
    async list(session, s, date) {
      const items: ProviderTrip[] = [];
      let first: ProviderTripsResponse | undefined;
      let cursor: string | undefined;
      for (let page = 0; page < MAX_PAGES; page += 1) {
        const query = new URLSearchParams({ limit: String(PAGE_SIZE) });
        // Todas las paginas del mismo dia: sin fecha, la primera fija cual es hoy.
        const day = date ?? first?.date;
        if (day !== undefined) query.set("date", day);
        if (cursor !== undefined) query.set("cursor", cursor);
        const data = parseOrFail(ProviderTripsResponse, await call(session, "GET", `${trips(s)}?${query.toString()}`), "unavailable");
        first ??= data;
        items.push(...data.items.filter((row) => row.providerId === s.providerId && row.siteId === s.siteId));
        cursor = data.nextCursor;
        if (cursor === undefined) break;
      }
      const { nextCursor: _ignored, ...rest } = first ?? { date: date ?? "", timezone: "UTC", items: [] };
      return { ...rest, items, ...(cursor === undefined ? {} : { nextCursor: cursor }) };
    },
    async offers(session, s) {
      const items: ProviderTrip[] = [];
      let first: ProviderOffersResponse | undefined;
      let cursor: string | undefined;
      for (let page = 0; page < MAX_PAGES; page += 1) {
        const query = new URLSearchParams({ limit: String(PAGE_SIZE) });
        if (cursor !== undefined) query.set("cursor", cursor);
        const data = parseOrFail(ProviderOffersResponse, await call(session, "GET", `${offers(s)}?${query.toString()}`), "unavailable");
        first ??= data;
        items.push(...data.items.filter((row) => row.providerId === s.providerId && row.siteId === s.siteId));
        cursor = data.nextCursor;
        if (cursor === undefined) break;
      }
      return { timezone: first?.timezone ?? "UTC", items, ...(cursor === undefined ? {} : { nextCursor: cursor }) };
    },
    async detail(session, s, tripId) {
      const data = parseOrFail(ProviderTripDetailResponse, await call(session, "GET", trip(s, tripId)), "unavailable");
      own(s, data.trip);
      return data;
    },
    async accept(session, s, tripId, offerId) {
      const payload = parseOrFail(AcceptTripOfferRequest, { offerId }, "validation");
      const data = parseOrFail(ProviderTripOfferResponse, await call(session, "POST", `${trip(s, tripId)}/accept`, payload), "unavailable");
      return own(s, data.trip);
    },
    async reject(session, s, tripId, offerId, reason) {
      const payload = parseOrFail(RejectTripOfferRequest, { offerId, reason }, "validation");
      parseOrFail(RejectTripOfferResponse, await call(session, "POST", `${trip(s, tripId)}/reject`, payload), "unavailable");
    },
    async assign(session, s, tripId, body) {
      const payload = parseOrFail(AssignTripRequest, body, "validation");
      const data = parseOrFail(AssignTripResponse, await call(session, "PUT", `${trip(s, tripId)}/assignment`, payload), "unavailable");
      own(s, data.trip);
      return data;
    },
  };
}
